import React from 'react';
import { isEmpty, toLower } from 'lodash';
import { ColorPalette, ColorCode, ColorValuesShown } from '../../types';
import { StyledColorPaletteWidget } from '../shared/styled-components/Widgets';
import AddColorSwatchButton from './AddColorSwatchButton';
import ColorSwatch from './ColorSwatch';
import { DefaultColorsEnum, WidgetPosition } from '../../constants/widgets';
import { generateUUID } from '../../helpers/utils';
import {
  getValueFromStyles,
  useWidgetStyleProperty,
} from '../guides/Inspector/helpers';
import { CSS_CLASSES, CSS_PROPERTIES } from '../guides/Inspector/constants';
import { useActiveThemeset } from '../../queries/themesets';

interface Props {
  readOnly: boolean;
  defaultColorCodes?: ColorValuesShown;
  colors: ColorPalette[];
  numberOfColumns: number;
  widgetPosition: WidgetPosition;
  widgetStyle: string;
  onAddColor: () => void;
  onRemoveColor: (position: number) => void;
  onModifyColor: (
    colorName: string,
    colorCodes: ColorCode[],
    position: number,
  ) => void;
}

const ColorPaletteWidget: React.FC<Props> = ({
  readOnly,
  defaultColorCodes,
  colors,
  numberOfColumns,
  widgetPosition,
  onAddColor,
  onRemoveColor,
  onModifyColor,
  widgetStyle,
}) => {
  const [colorsArray, setColorsArray] = React.useState([]);
  const { data: themeset } = useActiveThemeset();

  React.useEffect(() => {
    setColorsArray(colors.map(color => ({ ...color, id: generateUUID() })));
  }, [colors]);

  const defaultValues = !isEmpty(defaultColorCodes)
    ? defaultColorCodes
    : { hex: true, rgb: true, cmyk: true };

  const onAddColorSwatch = () => {
    onAddColor();
    setColorsArray(
      colorsArray.concat({
        name: '',
        colorCodes: [
          {
            key: 'HEX',
            value: '',
          },
          {
            key: 'RGB',
            value: '',
          },
          {
            key: 'CMYK',
            value: '',
          },
        ],
        id: generateUUID(),
      }),
    );
  };

  const onDeleteColorSwatch = (position: number) => {
    onRemoveColor(position);
    setColorsArray(colorsArray.filter((_item, index) => index !== position));
  };

  const isLastRow = (rowIndex: number, colorCodes: ColorCode[]) =>
    rowIndex === colorCodes.length - 1;

  const onDeleteRow = (
    position: number,
    rowIndex: number,
    colorCodes: ColorCode[],
  ) => {
    if (!isLastRow(rowIndex, colorCodes)) {
      setColorsArray(state => {
        state[position].colorCodes = state[position].colorCodes.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (_item: any, index: number) => index !== rowIndex,
        );

        return [...state];
      });
    }
  };

  const onEditColor = (
    colorName: string,
    colorCodes: ColorCode[],
    position: number,
    rowIndex: number,
  ) => {
    const filteredColorCodes = colorCodes.filter(
      item => item.key.length || item.value.length,
    );
    onModifyColor(colorName, filteredColorCodes, position);

    setColorsArray(state => {
      state[position].colorCodes = colorCodes;

      return [...state];
    });

    if (isLastRow(rowIndex, colorCodes)) {
      setColorsArray(state => {
        state[position].colorCodes = state[position].colorCodes.concat({
          key: '',
          value: '',
        });

        return [...state];
      });
    }
  };

  const getHexCode = (swatch: ColorPalette) =>
    swatch.colorCodes.find(
      color => toLower(color.key) === DefaultColorsEnum.HEX,
    )?.value;

  const nameFontFamily =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_FAMILY,
      CSS_CLASSES.COLOR_PALETTE_NAME,
    ) ?? themeset?.widgets.colorPalette.name.fontFamily;

  const nameFontSize =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_SIZE,
      CSS_CLASSES.COLOR_PALETTE_NAME,
    ) ?? themeset?.widgets.colorPalette.name.fontSize;

  const nameFontStyle =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_WEIGHT,
      CSS_CLASSES.COLOR_PALETTE_NAME,
    ) ?? themeset?.widgets.colorPalette.name.fontStyle;

  const colorNameColor =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.COLOR,
      CSS_CLASSES.COLOR_PALETTE_NAME,
    ) ?? themeset?.widgets.colorPalette.name.textColor?.hexCode;

  const modeFontFamily =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_FAMILY,
      CSS_CLASSES.COLOR_PALETTE_KEY,
    ) ?? themeset?.widgets.colorPalette.mode.fontFamily;

  const modeFontSize =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_SIZE,
      CSS_CLASSES.COLOR_PALETTE_KEY,
    ) ?? themeset?.widgets.colorPalette.mode.fontSize;

  const modeFontStyle =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_WEIGHT,
      CSS_CLASSES.COLOR_PALETTE_KEY,
    ) ?? themeset?.widgets.colorPalette.mode.fontStyle;

  const colorKeyColor =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.COLOR,
      CSS_CLASSES.COLOR_PALETTE_KEY,
    ) ?? themeset?.widgets.colorPalette.mode.textColor?.hexCode;

  const valueFontFamily =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_FAMILY,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    ) ?? themeset?.widgets.colorPalette.value.fontFamily;

  const valueFontSize =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_SIZE,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    ) ?? themeset?.widgets.colorPalette.value.fontSize;

  const valueFontStyle =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.FONT_WEIGHT,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    ) ?? themeset?.widgets.colorPalette.value.fontStyle;

  const valueColor =
    getValueFromStyles(
      widgetStyle,
      CSS_PROPERTIES.COLOR,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    ) ?? themeset?.widgets.colorPalette.value.textColor?.hexCode;

  const { value: borderRadius } = useWidgetStyleProperty(
    widgetStyle,
    'widgets.colorPalette.borderRadius',
    CSS_PROPERTIES.BORDER_RADIUS,
  );
  console.log('borderRadius', borderRadius);

  return (
    <StyledColorPaletteWidget
      widgetStyle={widgetStyle}
      data-testid="colorPaletteWidget"
    >
      {colorsArray.map((swatch: ColorPalette, index: number) => {
        if (readOnly && !getHexCode(swatch)) {
          return null;
        }

        return (
          <ColorSwatch
            defaultColorsShown={defaultValues}
            key={swatch.id}
            colorName={swatch.name}
            hexCode={getHexCode(swatch)}
            deleteColor={onDeleteColorSwatch}
            editColor={onEditColor}
            position={index}
            readOnly={readOnly}
            numberOfColumns={numberOfColumns}
            colorCodes={swatch.colorCodes}
            onDeleteRow={onDeleteRow}
            widgetPosition={widgetPosition}
            nameStyles={{
              fontFamily: nameFontFamily,
              fontSize: nameFontSize,
              fontStyle: nameFontStyle,
              color: colorNameColor,
            }}
            modeStyles={{
              fontFamily: modeFontFamily,
              fontSize: modeFontSize,
              fontStyle: modeFontStyle,
              color: colorKeyColor,
            }}
            valueStyles={{
              fontFamily: valueFontFamily,
              fontSize: valueFontSize,
              fontStyle: valueFontStyle,
              color: valueColor,
            }}
            borderRadius={borderRadius}
          />
        );
      })}
      {!readOnly && (
        <AddColorSwatchButton
          addColorSwatch={onAddColorSwatch}
          numberOfColumns={numberOfColumns}
          widgetPosition={widgetPosition}
        />
      )}
    </StyledColorPaletteWidget>
  );
};

export default ColorPaletteWidget;
